import { useEffect, useState } from 'preact/hooks';
import { get } from '../../api';
import type { ContentSeed, ContentBrief, ContentDraft } from '../../types';
import type { TableType } from './TableSelector';
import { LoadingSpinner } from '../shared/LoadingSpinner';

interface RecordDetailProps {
  activeTable: TableType;
  recordId: string;
  onClose: () => void;
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' });
  } catch {
    return dateStr;
  }
}

function ProvenanceChain({ hasSeeds, hasBrief, hasDraft, isPublished }: { hasSeeds: boolean; hasBrief: boolean; hasDraft: boolean; isPublished: boolean }) {
  return (
    <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px solid #1a1a1a' }}>
      <div class="detail-section-title" style={{ marginBottom: '8px' }}>Provenance Chain</div>
      <div class="prov-chain">
        <span class={`prov-step${hasSeeds ? '' : ' prov-inactive'}`} style={hasSeeds ? { background: 'var(--orange)' } : undefined}>Seed</span>
        <span class="prov-arrow">→</span>
        <span class={`prov-step${hasBrief ? '' : ' prov-inactive'}`} style={hasBrief ? { background: 'var(--lilac)' } : undefined}>Brief</span>
        <span class="prov-arrow">→</span>
        <span class={`prov-step${hasDraft ? '' : ' prov-inactive'}`} style={hasDraft ? { background: 'var(--blue)' } : undefined}>Draft</span>
        <span class="prov-arrow">→</span>
        <span class={`prov-step${isPublished ? '' : ' prov-inactive'}`} style={isPublished ? { background: 'var(--ltgreen)' } : undefined}>
          {isPublished ? 'Published' : 'Not published'}
        </span>
      </div>
    </div>
  );
}

function SeedDetail({ seed, linkedBriefs, onClose }: { seed: ContentSeed; linkedBriefs: ContentBrief[]; onClose: () => void }) {
  return (
    <>
      <div class="lcars-panel" style={{ marginBottom: 0 }}>
        <div class="panel-chrome">
          <div class="panel-pill" style={{ background: 'var(--peach)' }}></div>
          <div class="panel-bar" style={{ background: 'var(--peach)' }}>
            <span class="panel-title">Content Seed</span>
          </div>
        </div>
      </div>

      <div class="detail-header">
        <div>
          <div class="detail-type-row">
            <div class="detail-type-icon" style={{ background: 'var(--orange)' }}>S</div>
            <span class="detail-type-label">Content Seed</span>
          </div>
          <div class="detail-title">{seed.full_summary?.substring(0, 120) || seed.extracted_snippet?.substring(0, 120) || 'Untitled seed'}</div>
          <div class="detail-meta-badges">
            <span class={`status-badge status-${seed.status.toLowerCase()}`}>{seed.status}</span>
            {seed.domain && <span class="domain-badge">{seed.domain}</span>}
            <span style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--text-muted)' }}>{formatDate(seed.timestamp)}</span>
          </div>
        </div>
        <button class="detail-close" onClick={onClose}>×</button>
      </div>

      {seed.source_url && (
        <div class="detail-actions">
          <a class="detail-action-btn secondary" href={seed.source_url} target="_blank" rel="noopener" style={{ textDecoration: 'none' }}>View Source ↗</a>
        </div>
      )}

      {seed.full_summary && (
        <div class="detail-section">
          <div class="detail-section-title">Full Summary</div>
          <div class="detail-value" style={{ fontSize: '12.5px', lineHeight: '1.6', color: 'var(--text-dim)' }}>{seed.full_summary}</div>
        </div>
      )}

      <div class="detail-section">
        <div class="detail-section-title">Source</div>
        <div class="detail-field">
          <div class="detail-label">Source Name</div>
          <div class="detail-value">{seed.source_name || '—'}</div>
        </div>
        {seed.source_url && (
          <div class="detail-field">
            <div class="detail-label">URL</div>
            <div class="detail-value"><a href={seed.source_url} target="_blank" rel="noopener" style={{ color: 'var(--blue)', textDecoration: 'none', wordBreak: 'break-all', fontSize: '12px' }}>{seed.source_url}</a></div>
          </div>
        )}
        {seed.extracted_snippet && (
          <div class="detail-field">
            <div class="detail-label">Snippet</div>
            <div class="detail-value" style={{ fontSize: '12px', color: 'var(--text-dim)', fontStyle: 'italic' }}>{seed.extracted_snippet}</div>
          </div>
        )}
      </div>

      <div class="detail-section">
        <div class="detail-section-title">Metadata</div>
        {seed.relevance_score !== null && (
          <div class="detail-field">
            <div class="detail-label">Relevance Score</div>
            <div class="detail-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ flex: 1, height: '6px', background: '#1a1a1a', overflow: 'hidden' }}>
                <div style={{ width: `${(seed.relevance_score || 0) * 100}%`, height: '100%', background: 'var(--ltgreen)' }}></div>
              </div>
              <span style={{ fontFamily: 'var(--mono)', fontWeight: 600 }}>{seed.relevance_score?.toFixed(2)}</span>
            </div>
          </div>
        )}
        {seed.sentiment && (
          <div class="detail-field">
            <div class="detail-label">Sentiment</div>
            <div class="detail-value">{seed.sentiment}</div>
          </div>
        )}
        {seed.keywords && (
          <div class="detail-field">
            <div class="detail-label">Keywords</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
              {seed.keywords.split(',').map((kw, i) => <span class="domain-badge" key={i}>{kw.trim()}</span>)}
            </div>
          </div>
        )}
        {seed.DPIF_tags && (
          <div class="detail-field">
            <div class="detail-label">DPIF Tags</div>
            <div class="detail-value">{seed.DPIF_tags}</div>
          </div>
        )}
      </div>

      {linkedBriefs.length > 0 && (
        <div class="detail-section">
          <div class="detail-section-title">Linked Content</div>
          {linkedBriefs.map((brief) => (
            <div class="linked-record" key={brief.id}>
              <div class="linked-icon brief">B</div>
              <div class="linked-info">
                <div class="linked-name">{brief.headline}</div>
                <div class="linked-meta">Brief · {brief.status} · Priority {brief.composite_priority?.toFixed(2)}</div>
              </div>
              <span class="linked-arrow">→</span>
            </div>
          ))}
          <ProvenanceChain hasSeeds={true} hasBrief={linkedBriefs.length > 0} hasDraft={false} isPublished={false} />
        </div>
      )}

      <div class="detail-section">
        <div class="detail-section-title">Record</div>
        <div class="detail-field">
          <div class="detail-label">ID</div>
          <div class="detail-value" style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--text-muted)' }}>{seed.id}</div>
        </div>
      </div>
    </>
  );
}

function BriefDetail({ brief, linkedDrafts, onClose }: { brief: ContentBrief; linkedDrafts: ContentDraft[]; onClose: () => void }) {
  return (
    <>
      <div class="lcars-panel" style={{ marginBottom: 0 }}>
        <div class="panel-chrome">
          <div class="panel-pill" style={{ background: 'var(--peach)' }}></div>
          <div class="panel-bar" style={{ background: 'var(--peach)' }}>
            <span class="panel-title">Content Brief</span>
          </div>
        </div>
      </div>

      <div class="detail-header">
        <div>
          <div class="detail-type-row">
            <div class="detail-type-icon" style={{ background: 'var(--lilac)' }}>B</div>
            <span class="detail-type-label">Content Brief</span>
          </div>
          <div class="detail-title">{brief.headline}</div>
          <div class="detail-meta-badges">
            <span class={`status-badge status-${brief.status.toLowerCase().replace(/\s+/g, '')}`}>{brief.status}</span>
            <span class={`channel-badge channel-${brief.channel?.toLowerCase()}`}>{brief.channel}</span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--text-muted)' }}>{formatDate(brief.created_at)}</span>
          </div>
        </div>
        <button class="detail-close" onClick={onClose}>×</button>
      </div>

      <div class="detail-section">
        <div class="detail-section-title">Brief Details</div>
        <div class="detail-field">
          <div class="detail-label">Angle</div>
          <div class="detail-value">{brief.angle || '—'}</div>
        </div>
        <div class="detail-field">
          <div class="detail-label">Key Points</div>
          <div class="detail-value" style={{ fontSize: '12px', lineHeight: '1.6', color: 'var(--text-dim)' }}>{brief.key_points || '—'}</div>
        </div>
        {brief.dpif_connection && (
          <div class="detail-field">
            <div class="detail-label">DPIF Connection</div>
            <div class="detail-value">{brief.dpif_connection}</div>
          </div>
        )}
      </div>

      <div class="detail-section">
        <div class="detail-section-title">Scores</div>
        <div class="detail-field">
          <div class="detail-label">Composite Priority</div>
          <div class="detail-value" style={{ fontFamily: 'var(--mono)', fontWeight: 600 }}>{brief.composite_priority?.toFixed(3)}</div>
        </div>
        <div class="detail-field">
          <div class="detail-label">Timeliness / Strategic</div>
          <div class="detail-value" style={{ fontFamily: 'var(--mono)' }}>{brief.timeliness_score?.toFixed(2)} / {brief.strategic_score?.toFixed(2)}</div>
        </div>
      </div>

      {linkedDrafts.length > 0 && (
        <div class="detail-section">
          <div class="detail-section-title">Linked Drafts</div>
          {linkedDrafts.map((draft) => (
            <div class="linked-record" key={draft.id}>
              <div class="linked-icon draft">D</div>
              <div class="linked-info">
                <div class="linked-name">{draft.headline}</div>
                <div class="linked-meta">Draft · {draft.status} · {draft.word_count} words</div>
              </div>
              <span class="linked-arrow">→</span>
            </div>
          ))}
          <ProvenanceChain hasSeeds={true} hasBrief={true} hasDraft={linkedDrafts.length > 0} isPublished={linkedDrafts.some((d) => d.status === 'Published')} />
        </div>
      )}

      <div class="detail-section">
        <div class="detail-section-title">Record</div>
        <div class="detail-field">
          <div class="detail-label">ID</div>
          <div class="detail-value" style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--text-muted)' }}>{brief.id}</div>
        </div>
      </div>
    </>
  );
}

function DraftDetail({ draft, brief, onClose }: { draft: ContentDraft; brief: ContentBrief | null; onClose: () => void }) {
  return (
    <>
      <div class="lcars-panel" style={{ marginBottom: 0 }}>
        <div class="panel-chrome">
          <div class="panel-pill" style={{ background: 'var(--peach)' }}></div>
          <div class="panel-bar" style={{ background: 'var(--peach)' }}>
            <span class="panel-title">Content Draft</span>
          </div>
        </div>
      </div>

      <div class="detail-header">
        <div>
          <div class="detail-type-row">
            <div class="detail-type-icon" style={{ background: 'var(--blue)' }}>D</div>
            <span class="detail-type-label">Content Draft</span>
          </div>
          <div class="detail-title">{draft.headline}</div>
          <div class="detail-meta-badges">
            <span class={`status-badge status-${draft.status.toLowerCase().replace(/\s+/g, '')}`}>{draft.status}</span>
            <span class={`channel-badge channel-${draft.channel?.toLowerCase()}`}>{draft.channel}</span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--text-muted)' }}>{draft.word_count?.toLocaleString()} words</span>
          </div>
        </div>
        <button class="detail-close" onClick={onClose}>×</button>
      </div>

      <div class="detail-actions">
        <button class="detail-action-btn primary">Open in Staging →</button>
      </div>

      {draft.meta_description && (
        <div class="detail-section">
          <div class="detail-section-title">Meta Description</div>
          <div class="detail-value" style={{ fontSize: '12px', color: 'var(--text-dim)' }}>{draft.meta_description}</div>
        </div>
      )}

      {draft.suggested_tags && (
        <div class="detail-section">
          <div class="detail-section-title">Tags</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {draft.suggested_tags.split(',').map((tag, i) => <span class="domain-badge" key={i}>{tag.trim()}</span>)}
          </div>
        </div>
      )}

      {brief && (
        <div class="detail-section">
          <div class="detail-section-title">Source Brief</div>
          <div class="linked-record">
            <div class="linked-icon brief">B</div>
            <div class="linked-info">
              <div class="linked-name">{brief.headline}</div>
              <div class="linked-meta">Brief · {brief.status} · Priority {brief.composite_priority?.toFixed(2)}</div>
            </div>
            <span class="linked-arrow">→</span>
          </div>
          <ProvenanceChain hasSeeds={true} hasBrief={true} hasDraft={true} isPublished={draft.status === 'Published'} />
        </div>
      )}

      <div class="detail-section">
        <div class="detail-section-title">Record</div>
        <div class="detail-field">
          <div class="detail-label">ID</div>
          <div class="detail-value" style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--text-muted)' }}>{draft.id}</div>
        </div>
        <div class="detail-field">
          <div class="detail-label">Brief ID</div>
          <div class="detail-value" style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--text-muted)' }}>{draft.brief_id}</div>
        </div>
      </div>
    </>
  );
}

export function RecordDetail({ activeTable, recordId, onClose }: RecordDetailProps) {
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState<any>(null);
  const [linked, setLinked] = useState<any>(null);

  useEffect(() => {
    setLoading(true);
    setRecord(null);
    setLinked(null);

    if (activeTable === 'seeds') {
      get<{ seed: ContentSeed; linked_briefs: ContentBrief[] }>(`/api/seed/${recordId}`)
        .then((data) => { setRecord(data.seed); setLinked(data.linked_briefs || []); })
        .catch(console.error)
        .finally(() => setLoading(false));
    } else if (activeTable === 'briefs') {
      get<{ brief: ContentBrief; linked_drafts: ContentDraft[] }>(`/api/brief/${recordId}`)
        .then((data) => { setRecord(data.brief); setLinked(data.linked_drafts || []); })
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      get<{ draft: ContentDraft; brief: ContentBrief }>(`/api/draft/${recordId}`)
        .then((data) => { setRecord(data.draft); setLinked(data.brief || null); })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [activeTable, recordId]);

  if (loading) return <div class="db-right"><LoadingSpinner /></div>;
  if (!record) return <div class="db-right"><div class="empty-state"><div class="empty-state-text">Record not found</div></div></div>;

  return (
    <div class="db-right">
      {activeTable === 'seeds' && <SeedDetail seed={record} linkedBriefs={linked || []} onClose={onClose} />}
      {activeTable === 'briefs' && <BriefDetail brief={record} linkedDrafts={linked || []} onClose={onClose} />}
      {activeTable === 'drafts' && <DraftDetail draft={record} brief={linked} onClose={onClose} />}
    </div>
  );
}
